import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWebhookLogs } from "@/shared/api/services/adminService";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Search,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";

const statusConfig = {
  received: { icon: Clock, color: "bg-blue-100 text-blue-700" },
  validated: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  processed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
  invalid_signature: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  duplicate: { icon: AlertTriangle, color: "bg-slate-100 text-slate-700" },
};

export default function WebhookLogs() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState("all");
  const [status, setStatus] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data: rawLogs = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ["webhook-logs"],
    queryFn: () => getWebhookLogs(500),
  });

  const logs = React.useMemo(() => {
    return (rawLogs || []).map((item) => {
      const httpStatus = item.httpStatus;
      const status = item.errorMessage
        ? "failed"
        : httpStatus >= 200 && httpStatus < 300
        ? "processed"
        : "failed";
      return {
        id: item.id,
        webhook_id: item.id,
        transaction_id: item.paymentId,
        source: item.merchantName || item.merchantId || "",
        direction: "outbound",
        event_type: item.event,
        status,
        response_status: httpStatus,
        response_time_ms: null,
        created_date: item.createdAt,
        url: item.url,
        headers: "{}",
        payload: item.requestPayload || "{}",
        response_body: item.responseBody,
        error_message: item.errorMessage,
      };
    });
  }, [rawLogs]);

  const filteredLogs = React.useMemo(() => {
    return logs.filter((log) => {
      if (search) {
        const searchLower = search.toLowerCase();
        if (
          !log.webhook_id?.toLowerCase().includes(searchLower) &&
          !log.transaction_id?.toLowerCase().includes(searchLower) &&
          !log.source?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }
      if (direction !== "all" && log.direction !== direction) return false;
      if (status !== "all" && log.status !== status) return false;
      return true;
    });
  }, [logs, search, direction, status]);

  const paginatedLogs = filteredLogs.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredLogs.length / pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("webhookLogs")}</h1>
          <p className="text-slate-500">{filteredLogs.length} {t("webhookEvents")}</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          {t("refresh")}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchByWebhookId")}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="pl-10"
            />
          </div>
          <Select value={direction} onValueChange={(v) => { setDirection(v); setPage(0); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("direction")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allDirection")}</SelectItem>
              <SelectItem value="inbound">{t("inbound")}</SelectItem>
              <SelectItem value="outbound">{t("outbound")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0); }}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatus")}</SelectItem>
              <SelectItem value="received">{t("received")}</SelectItem>
              <SelectItem value="validated">{t("validated")}</SelectItem>
              <SelectItem value="processed">{t("processed")}</SelectItem>
              <SelectItem value="failed">{t("failed")}</SelectItem>
              <SelectItem value="invalid_signature">{t("invalidSignature")}</SelectItem>
              <SelectItem value="duplicate">{t("duplicate")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">{t("webhookId")}</TableHead>
              <TableHead className="font-semibold">{t("direction")}</TableHead>
              <TableHead className="font-semibold">{t("source")}</TableHead>
              <TableHead className="font-semibold">{t("event")}</TableHead>
              <TableHead className="font-semibold">{t("status")}</TableHead>
              <TableHead className="font-semibold">{t("responseStatus")}</TableHead>
              <TableHead className="font-semibold">{t("date")}</TableHead>
              <TableHead className="font-semibold w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {t("loading")}
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-red-600">
                  {t("failedToLoadWebhookLogs")}{error?.message ? `: ${error.message}` : ""}
                </TableCell>
              </TableRow>
            ) : paginatedLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  {t("noWebhookLogs")}
                </TableCell>
              </TableRow>
            ) : (
              paginatedLogs.map((log, index) => {
                const statusConf = statusConfig[log.status] || statusConfig.received;
                const StatusIcon = statusConf.icon;

                return (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.02 * index }}
                    className="hover:bg-slate-50 transition-colors"
                  >
                    <TableCell>
                      <span className="font-mono text-sm">{log.webhook_id}</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`gap-1 ${
                          log.direction === "inbound" ? "border-blue-200 text-blue-700" : "border-emerald-200 text-emerald-700"
                        }`}
                      >
                        {log.direction === "inbound" ? (
                          <ArrowDownToLine className="h-3 w-3" />
                        ) : (
                          <ArrowUpFromLine className="h-3 w-3" />
                        )}
                        {t(log.direction)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{log.source}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {log.event_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusConf.color} gap-1 border-0`}>
                        <StatusIcon className="h-3 w-3" />
                        {t(log.status === "invalid_signature" ? "invalidSignature" : log.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${
                          log.response_status >= 200 && log.response_status < 300
                            ? "text-emerald-600"
                            : log.response_status >= 400
                            ? "text-red-600"
                            : "text-slate-600"
                        }`}>
                          {log.response_status || "-"}
                        </span>
                        {log.response_time_ms && (
                          <span className="text-xs text-slate-400">
                            {log.response_time_ms}ms
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="text-slate-900">
                          {log.created_date ? format(new Date(log.created_date), "MMM d, yyyy") : t("nA")}
                        </div>
                        <div className="text-slate-500">
                          {log.created_date ? format(new Date(log.created_date), "HH:mm:ss") : ""}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setSelectedLog(log)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </motion.tr>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredLogs.length)} {t("of")} {filteredLogs.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">{t("page")} {page + 1} {t("of")} {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Sheet */}
      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("webhookDetails")}</SheetTitle>
          </SheetHeader>

          {selectedLog && (
            <div className="mt-6 space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">{t("webhookId")}</p>
                    <p className="font-mono text-sm">{selectedLog.webhook_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("transactionId")}</p>
                    <p className="font-mono text-sm">{selectedLog.transaction_id || t("nA")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("direction")}</p>
                    <Badge variant="outline">{t(selectedLog.direction)}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("status")}</p>
                    <Badge className={statusConfig[selectedLog.status]?.color || "bg-slate-100"}>
                      {t(selectedLog.status === "invalid_signature" ? "invalidSignature" : selectedLog.status)}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("url")}</p>
                  <p className="font-mono text-sm bg-slate-50 p-2 rounded break-all">
                    {selectedLog.url || t("nA")}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestHeaders")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                    {selectedLog.headers ? JSON.stringify(JSON.parse(selectedLog.headers), null, 2) : "{}"}
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("requestPayload")}</p>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[200px]">
                    {selectedLog.payload ? JSON.stringify(JSON.parse(selectedLog.payload), null, 2) : "{}"}
                  </pre>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("responseStatus")}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{t("status")}: {selectedLog.response_status || t("nA")}</Badge>
                    {selectedLog.response_time_ms && (
                      <Badge variant="outline">{selectedLog.response_time_ms}ms</Badge>
                    )}
                  </div>
                  <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[200px]">
                    {selectedLog.response_body || t("noResponseBody")}
                  </pre>
                </div>

                {selectedLog.error_message && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800">{t("error")}</p>
                    <p className="text-sm text-red-700">{selectedLog.error_message}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
